import { IPauseAdResults } from 'tag/ts/tag/interfaces/ads/pause/IPauseAdResults';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { FreeWheelPauseAdModel } from 'tag/ts/tag/model/ads/pause/FreeWheelPauseAdModel';
import { PauseAdProvider } from 'tag/ts/tag/model/type/ads/PauseAdProvider';
import { IPauseAdRequest } from 'tag/ts/tag/player/adapters/avia/plugins/pauseads/interfaces/IPauseAdRequest';
import { IPauseAdResult } from 'tag/ts/tag/player/adapters/avia/plugins/pauseads/interfaces/IPauseAdResult';
import { IPauseAdsPluginApi } from 'tag/ts/tag/player/adapters/avia/plugins/pauseads/interfaces/IPauseAdsPluginApi';
import { PAUSE_ADS_PLUGIN_ID } from 'tag/ts/tag/player/adapters/avia/plugins/pauseads/pauseAdsFactory';
import { TPauseAdProvider } from 'tag/ts/tag/types/ads/pause/TPauseAdProvider';
import { TPauseAdResponse } from 'tag/ts/tag/types/ads/pause/TPauseAdResponse';

/**
 * @copyright © Paramount Streaming
 * @description Smart Tag service layer for pause ad requests.
 *
 * Acts as the bridge between the public Tag API (`tag.API.TAG.getPauseAd()`)
 * and the `pause-ads` avia-js plugin. This class is responsible for:
 *
 * 1. Resolving the plugin handle from the avia player instance.
 * 2. Gathering provider-specific parameters from Smart Tag's model layer.
 * 3. Building an `IPauseAdRequest` and delegating to the plugin.
 * 4. Mapping the plugin's `IPauseAdResult` back to Smart Tag's `TPauseAdResponse`.
 *
 * All methods are static -- `PauseAdService` is a stateless utility class.
 */
/** Fallback player dimensions when the container element is not yet available (matches Pluto TV reference implementation defaults). */
const DEFAULT_PLAYER_HEIGHT = 1080;
const DEFAULT_PLAYER_WIDTH = 1920;

export class PauseAdService {

	/**
	 * @description Fetches a pause ad for the given provider.
	 *
	 * Called by `TagAPI.TAG.getPauseAd()` (the public API consumed by Skin and CTV apps).
	 * Rejects if the plugin is not available (e.g. avia player not yet initialized).
	 *
	 * @param tag      - Current Tag instance providing context (config, params, player adapter).
	 * @param provider - Pause ad provider (`PauseAdProvider.FREEWHEEL`, `PauseAdProvider.GAM`, `PauseAdProvider.AD_IMAGE_SERVICE`).
	 * @returns Promise resolving to `TPauseAdResponse` on success, rejecting on failure.
	 */
	static getPauseAd( tag: ITag, provider: TPauseAdProvider ): Promise<TPauseAdResponse> {
		const plugin = PauseAdService.#getPlugin( tag );

		if ( !plugin ) {
			return Promise.reject( {
				msg: 'PauseAdService: pause-ads plugin not available',
				success: false
			} as TPauseAdResponse );
		}

		const request = PauseAdService.#buildRequest( tag, provider );

		return plugin.getPauseAd( request )
			.then( ( result: IPauseAdResult ) => {
				const response = PauseAdService.#toResponse( result );

				if ( !response.success ) {
					return Promise.reject( response );
				}

				return response;
			} )
			.catch( ( error: unknown ) => {
				// Structured no-fill rejection from .then() above — pass through with pauseAd data intact
				if ( error && typeof error === 'object' && 'pauseAd' in error ) {
					return Promise.reject( error );
				}

				return Promise.reject( {
					msg: `PauseAdService: plugin call failed: ${
						error instanceof Error ? error.message : String( error )
					}`,
					success: false
				} as TPauseAdResponse );
			} );
	}

	/**
	 * @description Retrieves the `pause-ads` plugin from the avia player.
	 *
	 * Walks the adapter chain: `tag -> controller -> playerManager -> playerAdapter -> player`.
	 * Returns `undefined` if any link in the chain is missing or the plugin is not registered.
	 */
	static #getPlugin( tag: ITag ): IPauseAdsPluginApi | undefined {
		try {
			const plugin = tag?.controller?.playerManager?.playerAdapter?.player?.getPlugin?.( PAUSE_ADS_PLUGIN_ID );
			if ( plugin && typeof ( plugin as IPauseAdsPluginApi ).getPauseAd === 'function' ) {
				return plugin as IPauseAdsPluginApi;
			}
		} catch {
			// Plugin not available -- player may not be initialized yet
		}
		return undefined;
	}

	/**
	 * @description Builds an `IPauseAdRequest` from Smart Tag context.
	 *
	 * - **FreeWheel**: gathers structured params (global, merged, slot) from
	 *   `FreeWheelPauseAdModel` so the plugin's FreeWheel provider can build the URL.
	 * - **GAM**: passes provider key only; callers supply params or a URL override
	 *   via skin config when GAM pause ads are activated.
	 * - **Ad Image Service**: gathers stitcher clip data, JWT, server URL, player
	 *   dimensions, and GDPR consent for the Pluto TV Ad Image Service JSON-based
	 *   pause ad API. GDPR values are read from `paramsVO.AD.FW.GDPR` — the app
	 *   must supply these; Smart Tag does not manage consent.
	 * - **Custom**: passes the provider string through as the plugin provider key for any runtime-registered providers.
	 */
	static #buildRequest( tag: ITag, provider: TPauseAdProvider ): IPauseAdRequest {
		switch ( provider ) {
			case PauseAdProvider.FREEWHEEL: {
				const embedType = tag?.params?.SYSTEM?.embedType;
				const model = new FreeWheelPauseAdModel( { tag, embedType } );

				return {
					provider: 'freewheel',
					params: {
						global: { ...( model.globalParams || {} ) },
						merged: { ...( model.mergedParams || {} ) },
						slot: { ...( model.slotParams || {} ) }
					}
				};
			}

			case PauseAdProvider.GAM:
				return { provider: 'gam' };

			case PauseAdProvider.AD_IMAGE_SERVICE: {
				const stitcher = tag?.params?.STITCHER;
				const clip = tag?.snapshot?.stitcher?.clip;
				const currentTime = tag?.controller?.playerManager?.currentTime?.contentTime ?? 0;
				const container = tag?.view?.container;
				const gdprParams = tag?.params?.AD?.FW?.GDPR;

				return {
					provider: 'ad-image-service',
					params: {
						global: {
							baseUrl: stitcher?.servers?.pause ?? '',
							token: stitcher?.token ?? '',
							clipId: clip?.clipId || stitcher?.id || '',
							// Blackbird metadata plugin returns '' (not null) before clip info loads; || falls back to stitcher.id correctly, ?? does not
							episodeId: clip?.episodeId || stitcher?.id || '',
							pauseOffset: Math.round( currentTime * 1000 ),
							contentType: stitcher?.type ?? '',
							deviceType: 'web',
							playerHeight: container?.clientHeight ?? DEFAULT_PLAYER_HEIGHT,
							playerWidth: container?.clientWidth ?? DEFAULT_PLAYER_WIDTH,
							// navigator.doNotTrack returns '1'/'0'/null — Ad Image Service expects a boolean
							deviceDNT: navigator?.doNotTrack === '1',
							indexTimeline: Date.now(),
							origin: 'Pluto.tv',
							contentPageUrl: 'https://pluto.tv',
							appDomain: 'Pluto.tv',
							adImpressionProxy: false,
							podType: 'ad',
							gdpr: gdprParams?._fw_gdpr ?? false,
							gdprConsent: gdprParams?._fw_gdpr_consent ?? ''
						}
					}
				};
			}

			// Defensive: TS-typed callers can't reach this branch since TPauseAdProvider is exhaustive,
			// but runtime callers (untyped JS consumers) can still pass arbitrary strings — passed through
			// as the plugin provider key for any runtime-registered providers.
			default:
				return { provider: provider };
		}
	}

	/**
	 * @description Converts the plugin's `IPauseAdResult` to Smart Tag's `TPauseAdResponse`.
	 *
	 * Pure synchronous mapper — always returns a `TPauseAdResponse`. The caller
	 * (`getPauseAd`) inspects `response.success` and decides whether to resolve
	 * or reject the promise chain.
	 *
	 * Note: `IPauseAdResult.impressions` maps to `IPauseAdResults.impression` (Smart Tag convention).
	 */
	static #toResponse( result: IPauseAdResult ): TPauseAdResponse {
		const pauseAd: IPauseAdResults = {
			imageSource: result.imageSource,
			clickThrough: result.clickThrough,
			clickTrackingURLs: result.clickTrackingURLs,
			impression: result.impressions
		};

		return {
			msg: result.success
				? 'PauseAdService: plugin call succeeded'
				: 'PauseAdService: plugin returned no fill',
			success: result.success,
			pauseAd
		};
	}
}